"use client";

import { useRef, useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { UnitEquivalents } from "@/components/tools/physics-mechanics/energy-tables";
import {
  InertiaInputs,
  InertiaResults,
  type InertiaState,
} from "@/components/tools/physics-mechanics/inertia-panel";
import {
  RelationInputs,
  RelationResults,
} from "@/components/tools/physics-mechanics/relation-panel";
import {
  BalanceInputs,
  BalanceResults,
  type BalanceState,
} from "@/components/tools/physics-mechanics/torque-balance-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  evaluateRelation,
  initialRelationState,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  TORQUE_RELATION,
  type TorqueKey,
} from "@/lib/tool-kit/physics-mechanics/torque";
import { TORQUE } from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const TORQUE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeTorque: "Torque of one force (τ = rF sin θ)",
  modeBalance: "Net torque and balance (levers, seesaws)",
  modeInertia: "Moment of inertia and angular acceleration",
  torqueModeHint:
    "Torque is the turning effect of a force. It grows with the distance r from the pivot and with the part of the force at right angles to the arm: θ is the angle between the arm and the force, so 90° gives the most torque and 0° gives none.",
  balanceModeHint:
    "Add every force that turns the object about the pivot, with its distance from the pivot and which way it turns it. The object is balanced when the clockwise and counter-clockwise torques are equal. To use the weight of a mass, choose kgf: 10 kgf is the weight of 10 kg.",
  inertiaModeHint:
    "Moment of inertia is rotation's version of mass: the larger it is, the harder the object is to spin up. It depends on how the mass is spread out about the axis.",
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  torque: "Torque τ",
  distance: "Lever arm r (distance from the pivot)",
  force: "Force F",
  angle: "Angle θ between the arm and the force",
  angleHint: "Between 0° and 180°. A push at right angles to the arm is 90°.",
  workingHeading: "The working",
  stepEquation: "Start from {equation}.",
  stepRearrange: "Rearrange for {symbol}: {form}.",
  stepConvert: "Convert to SI units: {list}.",
  stepSubstitute: "{symbol} = {working} = {value}",
  stepAnswerUnit: "In the unit you chose: {si} = {shown}.",
  copy: "Copy result",
  copied: "Copied!",
  enterAll: "Enter every value except the one you are solving for.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  errors: {
    "distance-negative": "Distance cannot be negative.",
    "force-negative":
      "Give the force as a positive size; the angle sets the direction relative to the arm.",
    "torque-negative":
      "Give the torque as a positive size; the turning direction is set separately in the balance calculator.",
    "arm-force-zero":
      "The angle needs a lever arm and a force that are both above zero.",
    "ratio-out-of-range":
      "No angle fits: the torque is larger than r × F, which is the most any angle can give (at 90°).",
    "angle-parallel":
      "At 0° or 180° the force points along the arm and gives no torque, so r or F cannot be found. Choose an angle between them.",
    "distance-zero":
      "The lever arm is zero, so the force cannot be found from the torque. Enter a distance above zero.",
    "force-zero":
      "The force is zero, so the lever arm cannot be found from the torque. Enter a force above zero.",
    "sign-mismatch":
      "That combination needs a negative value. Check the angle.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
  },
  equivalentHeading: "The same torque in other units",
  columnUnit: "Unit",
  columnValue: "Torque",
  otherAngle:
    "The same torque is also produced at {angle}, because sin θ = sin(180° − θ).",
  rowLabel: "Force {n}",
  rowForce: "Size of force {n}",
  rowDistance: "Distance of force {n} from the pivot",
  rowAngle: "Angle of force {n} to the arm",
  rowDirection: "Direction force {n} turns the object",
  directionCcw: "Counter-clockwise",
  directionCw: "Clockwise",
  addRow: "Add a force",
  removeRow: "Remove force {n}",
  balanceHeading: "To balance it (optional — fill in one)",
  balanceDistance: "Distance of a balancing force from the pivot",
  balanceForce: "Size of the balancing force",
  balanceHint: "Gives the force needed, pushing at right angles to the arm.",
  netTorque: "Net torque",
  netCcw: "counter-clockwise",
  netCw: "clockwise",
  totalCcw: "Counter-clockwise torque",
  totalCw: "Clockwise torque",
  balanced:
    "The torques cancel: the net torque is zero, so the object is in rotational equilibrium. It does not start to turn.",
  tableHeading: "Torque of each force",
  columnTerm: "Force",
  columnForce: "Size",
  columnDistance: "Distance",
  columnAngle: "Angle",
  columnDirection: "Turns",
  columnTorque: "Torque",
  stepTerm:
    "Force {n}: τ = r F sin θ = {distance} × {force} × sin({angle}°) = {torque} N·m, {direction}.",
  stepNet:
    "Counter-clockwise {ccw} N·m minus clockwise {cw} N·m gives a net torque of {net} N·m.",
  stepBalanceForce:
    "To balance it, a force at right angles to the arm at {distance} m must supply {torque} N·m: F = τ ÷ r = {force}, turning it {direction}.",
  stepBalanceDistance:
    "To balance it, a force of {force} N at right angles to the arm must act at r = τ ÷ F = {distance}, turning it {direction}.",
  balanceNeed: "Enter at least one force and its distance from the pivot.",
  balanceUnreadable:
    "Force {n} needs a size, a distance and an angle as plain numbers.",
  balanceNegative:
    "Force {n} has a negative size or distance. Give sizes as positive numbers and use the direction menu.",
  shapeLabel: "Shape",
  shapeDisk: "Solid cylinder or disk, about its axis (½MR²)",
  shapeHoop: "Thin hoop or hollow cylinder, about its axis (MR²)",
  shapeSolidSphere: "Solid sphere, about a diameter (⅖MR²)",
  shapeShell: "Thin spherical shell, about a diameter (⅔MR²)",
  shapeRodCentre: "Thin rod, about its centre (ML²/12)",
  shapeRodEnd: "Thin rod, about one end (ML²/3)",
  shapePlate:
    "Rectangular plate, about its centre, perpendicular to it (M(a²+b²)/12)",
  shapePoint: "Point mass at distance r (mr²)",
  inertiaMass: "Mass M",
  dimensionR: "Radius R",
  dimensionL: "Length L",
  dimensionA: "Side a",
  dimensionB: "Side b",
  dimensionPoint: "Distance r from the axis",
  offset: "Axis shifted from the centre by (optional)",
  offsetHint:
    "Parallel-axis theorem: adds M d². Leave blank for the standard axis.",
  torqueOptional: "Torque applied (optional)",
  torqueHint: "Gives the angular acceleration α = τ ÷ I.",
  inertia: "Moment of inertia I",
  centreInertia: "Moment of inertia about the centre",
  angularAcceleration: "Angular acceleration α",
  stepFormula:
    "Standard result: {formula} with M = {mass} kg and {sizes} gives I = {value} kg·m².",
  stepShift:
    "Parallel-axis theorem: I = I_centre + M d² = {centre} + {mass} × {offset}² = {value} kg·m².",
  stepAlpha:
    "Angular acceleration: α = τ ÷ I = {torque} ÷ {inertia} = {alpha} rad/s².",
  copyText: "I = {inertia}",
  inertiaNeed: "Enter the mass and the size of the shape.",
  inertiaUnreadable: "Enter every value as a plain number.",
  inertiaErrors: {
    mass: "Mass must be greater than zero.",
    size: "Every dimension must be greater than zero.",
    offset:
      "The shift must be zero or more, and the parallel-axis shift only applies to shapes given about their centre.",
  },
  assumptions:
    "Assumes rigid bodies, uniform density, forces in one plane, and forces that act at the distance given. Torque is a vector; here it is treated as clockwise or counter-clockwise about a single fixed pivot.",
} as const;

type Mode = "torque" | "balance" | "inertia";

const TORQUE_UNITS = ["Nm", "lbfft", "lbfin", "Ncm", "kNm"] as const;

const INITIAL_TORQUE = initialRelationState<TorqueKey>(TORQUE_RELATION, "t", {
  r: { value: "0.25", unit: "m" },
  F: { value: "150", unit: "N" },
  a: { value: "60", unit: "deg" },
});
const INITIAL_BALANCE: BalanceState = {
  rows: [
    {
      id: 1,
      force: "25",
      forceUnit: "kgf",
      distance: "1.8",
      distanceUnit: "m",
      angle: "90",
      angleUnit: "deg",
      direction: "ccw",
    },
    {
      id: 2,
      force: "60",
      forceUnit: "kgf",
      distance: "0.75",
      distanceUnit: "m",
      angle: "90",
      angleUnit: "deg",
      direction: "cw",
    },
  ],
  balanceDistance: "",
  balanceDistanceUnit: "m",
  balanceForce: "",
  balanceForceUnit: "N",
};
const BLANK_SIZE = { value: "", unit: "m" };
const INITIAL_INERTIA: InertiaState = {
  shape: "disk",
  mass: { value: "10", unit: "kg" },
  sizes: {
    R: { value: "0.2", unit: "m" },
    L: { value: "1", unit: "m" },
    a: { value: "0.5", unit: "m" },
    b: { value: "1.2", unit: "m" },
    r: { value: "1", unit: "m" },
  },
  offset: BLANK_SIZE,
  torque: { value: "5", unit: "Nm" },
};

export default function TorqueCalculatorWidget() {
  const strings = useToolUi(TORQUE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("torque");
  const [torque, setTorque] = useState(INITIAL_TORQUE);
  const [balance, setBalance] = useState(INITIAL_BALANCE);
  const [inertia, setInertia] = useState(INITIAL_INERTIA);
  const [sigFigs, setSigFigs] = useState("4");
  const nextId = useRef(10);
  const sig = Number(sigFigs);
  const panel = {
    ...strings,
    errors: strings.errors as Record<string, string>,
  };
  const common = { fmt, sig, strings: panel };
  const labels: Record<TorqueKey, string> = {
    t: strings.torque,
    r: strings.distance,
    F: strings.force,
    a: strings.angle,
  };
  const torqueEval = evaluateRelation(TORQUE_RELATION, torque);
  const hints = {
    torque: strings.torqueModeHint,
    balance: strings.balanceModeHint,
    inertia: strings.inertiaModeHint,
  };

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="tq-mode"
        onChange={setMode}
        options={[
          { value: "torque", label: strings.modeTorque },
          { value: "balance", label: strings.modeBalance },
          { value: "inertia", label: strings.modeInertia },
        ]}
        value={mode}
      />
      <p className={HINT}>{hints[mode]}</p>
      {mode === "torque" ? (
        <RelationInputs
          {...common}
          def={TORQUE_RELATION}
          evaluation={torqueEval}
          hints={{ a: strings.angleHint }}
          idPrefix="tq-t"
          labels={labels}
          onState={setTorque}
          state={torque}
        />
      ) : null}
      {mode === "balance" ? (
        <BalanceInputs
          nextId={() => {
            nextId.current += 1;
            return nextId.current;
          }}
          onState={setBalance}
          state={balance}
          strings={strings}
        />
      ) : null}
      {mode === "inertia" ? (
        <InertiaInputs onState={setInertia} state={inertia} strings={strings} />
      ) : null}
      <div className="w-60">
        <SelectField
          id="tq-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "torque" ? (
          <RelationResults
            {...common}
            def={TORQUE_RELATION}
            evaluation={torqueEval}
            extras={(all, unknown) => (
              <>
                {unknown === "a" && all.a < Math.PI / 2 - 1e-9 ? (
                  <Notice>
                    {t(strings.otherAngle, {
                      angle: withUnit(
                        formatSig(fmt, 180 - (all.a * 180) / Math.PI, sig),
                        "°"
                      ),
                    })}
                  </Notice>
                ) : null}
                <UnitEquivalents
                  columnUnit={strings.columnUnit}
                  columnValue={strings.columnValue}
                  family={TORQUE}
                  fmt={fmt}
                  heading={strings.equivalentHeading}
                  sig={sig}
                  unitIds={TORQUE_UNITS}
                  value={all.t}
                />
              </>
            )}
            labels={labels}
            state={torque}
          />
        ) : null}
        {mode === "balance" ? (
          <>
            <BalanceResults
              fmt={fmt}
              sig={sig}
              state={balance}
              strings={strings}
            />
            <p className={HINT}>{strings.assumptions}</p>
          </>
        ) : null}
        {mode === "inertia" ? (
          <>
            <InertiaResults
              fmt={fmt}
              sig={sig}
              state={inertia}
              strings={strings}
            />
            <p className={HINT}>{strings.assumptions}</p>
          </>
        ) : null}
      </section>
    </>
  );
}
